import { useState, type CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { suiteLandingPath } from "@/components/layout/navConfig";
import { hasSeenExplainer } from "@/components/suite/SuiteExplainer";
import { openSuiteApp } from "@/lib/suite/openSuiteApp";
import type { SuiteManifestApp } from "@/lib/suite/suiteManifest";
import { useSuiteManifest } from "@/hooks/suite";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieSwitcherProps {
  /** Which family app this switcher is rendered inside. In Voice this is always "voice". */
  currentAppId?: string;
}

/**
 * GenieSwitcher — the shared Help Genie app-family launcher.
 *
 * A 3×3-dot trigger that opens a popover listing every app in the Suite
 * manifest: the current app pinned first ("You're here"), satellites launching
 * through the existing SSO flow in a new tab. The same component (and the same
 * /apps.json manifest) is destined for the satellite apps' top bars, so keep
 * it free of Voice-specific assumptions beyond the `currentAppId` default.
 */
export const GenieSwitcher = ({ currentAppId = "voice" }: GenieSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: apps = [] } = useSuiteManifest();

  const ordered = [...apps].sort(
    (a, b) => Number(b.id === currentAppId) - Number(a.id === currentAppId),
  );

  // Returning users launch the satellite straight into a new tab, so this tab
  // stays on the page they were on. First-time users go through the in-app
  // explainer route, which pre-mints the SSO token while they read and returns
  // here after the handoff. Fall back to the channel route derived from the app
  // id if the manifest carries no `path`.
  const handleLaunch = (app: SuiteManifestApp) => {
    setOpen(false);
    if (!app.externalUrl) return;
    if (hasSeenExplainer(app.id)) {
      void openSuiteApp({
        productWord: app.productWord,
        appUrl: app.externalUrl,
        source: "switcher",
        ssoEntryPath: app.ssoEntryPath,
      });
      return;
    }
    navigate(app.path ?? `/genie-hub/channels/${app.id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Switch Help Genie app"
          className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-genie-border bg-white transition-all duration-200 hover:border-[#C9DCFA] hover:shadow-[0_2px_10px_rgba(78,156,255,0.22)] data-[state=open]:border-[#C9DCFA] data-[state=open]:shadow-[0_2px_10px_rgba(78,156,255,0.22)]"
        >
          <span className="grid grid-cols-3 gap-[3px]" aria-hidden>
            {Array.from({ length: 9 }).map((_, i) => (
              <span
                key={i}
                className={`h-1 w-1 rounded-full bg-genie-muted transition-colors duration-200 ${DOT_HOVER_BY_COLUMN[i % 3]}`}
              />
            ))}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-[384px] max-w-[calc(100vw-2rem)] rounded-2xl border-genie-border bg-white p-1.5 text-genie-text shadow-[0_24px_60px_-12px_rgba(40,64,111,0.28)]"
      >
        <div className="flex items-center justify-between px-3 pb-1.5 pt-2.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.13em] text-genie-muted">
            Genie apps
          </span>
          <span className="text-[11px] text-gray-400">One Genie. Every channel.</span>
        </div>

        {ordered.map((app) => {
          const isCurrent = app.id === currentAppId;
          const rowStyle = {
            "--row-tint": tint(app.color, 0.06),
          } as CSSProperties;

          if (isCurrent) {
            return (
              <div
                key={app.id}
                style={rowStyle}
                className="flex w-full items-center gap-3 rounded-xl bg-[var(--row-tint)] px-3 py-2.5"
              >
                <AppRowBody app={app} />
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold text-genie-logo"
                  style={{ backgroundColor: tint(app.color, 0.14) }}
                >
                  You&apos;re here
                </span>
              </div>
            );
          }

          const rowContent = (
            <>
              <AppRowBody app={app} />
              <span className="flex shrink-0 items-center gap-2">
                {app.home && (
                  <span className="rounded-full bg-genie-logo/[0.08] px-2 py-0.5 text-[10px] font-semibold text-genie-logo">
                    Home
                  </span>
                )}
                {app.beta && (
                  <Badge className="bg-genie-primary/10 px-2 text-[10px] text-genie-primary hover:bg-genie-primary/10">
                    Beta
                  </Badge>
                )}
                <ArrowUpRight className="h-3.5 w-3.5 text-genie-primary opacity-0 transition-opacity duration-150 group-hover/row:opacity-100" />
              </span>
            </>
          );
          const rowClass =
            "group/row flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 hover:bg-[var(--row-tint)]";

          return app.externalUrl ? (
            <button
              key={app.id}
              type="button"
              style={rowStyle}
              className={rowClass}
              onClick={() => handleLaunch(app)}
            >
              {rowContent}
            </button>
          ) : (
            <Link
              key={app.id}
              to={app.path ?? suiteLandingPath}
              style={rowStyle}
              className={rowClass}
              onClick={() => setOpen(false)}
            >
              {rowContent}
            </Link>
          );
        })}

        <div className="mt-1.5 flex items-center justify-between border-t border-gray-100 px-3 pb-2 pt-2.5">
          <Link
            to={suiteLandingPath}
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-genie-logo transition-colors hover:text-genie-primary"
          >
            <GenieIcon name="share" size={16} className="h-4 w-4" />
            Explore the Suite
          </Link>
          <span className="text-[11px] text-gray-400">One login — SSO does the rest</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Lockup + description — shared by current and launchable rows. */
const AppRowBody = ({ app }: { app: SuiteManifestApp }) => (
  <>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-bold text-[#222B45]">
        Help Genie{" "}
        <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text pr-0.5 italic text-transparent">
          {app.productWord}
        </span>
      </span>
      <span className="block truncate text-xs text-genie-muted">{app.description}</span>
    </span>
  </>
);

/** Dots pick up the brand gradient columns (blue → violet → magenta) on hover/open. */
const DOT_HOVER_BY_COLUMN = [
  "group-hover:bg-[#4E9CFF] group-data-[state=open]:bg-[#4E9CFF]",
  "group-hover:bg-[#A968D8] group-data-[state=open]:bg-[#A968D8]",
  "group-hover:bg-[#FF4DB8] group-data-[state=open]:bg-[#FF4DB8]",
];

const tint = (hex: string, alpha: number): string => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
